import React from "react";
import { View, Text } from "react-native";
import { parseFirestoreDate } from "../utils/formatting";

const ACCENT = "#B4761B"; // amber — neutral / informational
const BG = "#FDF3E3";
const BORDER = "#B4761B";
const DIVIDER_COLOR = "#B4761B";
const TEXT_DARK = "#2E3A38";
const TEXT_MUTED = "#8A9794";
const COLOR_POSITIVE = "#B4761B"; // orange — owes more
const COLOR_NEGATIVE = "#1F8A5B"; // green — owes less

/**
 * VerbalOrderChanges
 *
 * Describes what changed between originalBooking and newBooking in plain
 * language. Each change line includes a colored currency impact where it can
 * be computed. Shows a net-change total at the bottom.
 *
 * Color convention:
 *   orange (#B4761B) = cost went up (customer owes more)
 *   green  (#1F8A5B) = cost went down (customer owes less / gets refund)
 */
const VerbalOrderChanges = ({
  originalBooking,
  newBooking,
  styles,
  product1Catalog = [],
  product2Catalog = [],
  product1Title = "Bicycles",
  product2Title = "Add-Ons",
  addressConfirmed = true,
  originalProductCostCents = 0,
}) => {
  if (!originalBooking || !newBooking) return null;

  // ─── helpers ────────────────────────────────────────────────────────────────

  const formatDate = (dateField) => {
    const d = parseFirestoreDate(dateField);
    if (!d) return "N/A";
    try {
      const day = d.toLocaleDateString("en-US", { weekday: "short" });
      const month = d.toLocaleDateString("en-US", { month: "short" });
      const date = d.getDate();
      const yr = d.getFullYear().toString().slice(-2);
      return `${day} ${month} ${date}, '${yr}`;
    } catch {
      return "N/A";
    }
  };

  const parseDays = (booking) => {
    const s = booking.startDate ? parseFirestoreDate(booking.startDate) : null;
    const e = booking.endDate ? parseFirestoreDate(booking.endDate) : null;
    return s && e ? Math.round((e - s) / (1000 * 60 * 60 * 24)) || 1 : 0;
  };

  // ─── financial variables (needed across transportation and discount sections) ─

  const origDeliveryCents  = Number(originalBooking.deliveryCostCents) || 0;
  const origPickupCents    = Number(originalBooking.pickupCostCents)   || 0;
  const newDeliveryCents   = Number(newBooking.deliveryCostCents)       || 0;
  const newPickupCents     = Number(newBooking.pickupCostCents)         || 0;

  const origTransDiscountCents = Number(originalBooking.transportationDiscount?.discountCents) || 0;
  const origDurDiscountCents   = Number(originalBooking.durationDiscount?.discountCents)       || 0;
  const newTransDiscountCents  = Number(newBooking.transportationDiscount?.discountCents)      || 0;
  const newDurDiscountCents    = Number(newBooking.durationDiscount?.discountCents)            || 0;

  // Net (post-discount) rate for individual transport services.
  // The discount % applies equally to delivery and pickup, so each service's
  // net cost = raw × (1 − discountPct/100).
  const origTransDiscountPct = Number(originalBooking.transportationDiscount?.discountPercent) || 0;
  const newTransDiscountPct  = Number(newBooking.transportationDiscount?.discountPercent)      || 0;
  const origTransNetRate     = (100 - origTransDiscountPct) / 100;
  const newTransNetRate      = (100 - newTransDiscountPct)  / 100;

  const formatAmount = (cents) => `$${(cents / 100).toFixed(2)}`;

  // ─── change detection ────────────────────────────────────────────────────────

  // 1. Rental Dates
  const origStart = originalBooking.startDate ? parseFirestoreDate(originalBooking.startDate) : null;
  const origEnd   = originalBooking.endDate   ? parseFirestoreDate(originalBooking.endDate)   : null;
  const newStart  = newBooking.startDate      ? parseFirestoreDate(newBooking.startDate)       : null;
  const newEnd    = newBooking.endDate        ? parseFirestoreDate(newBooking.endDate)         : null;

  const startChanged  = origStart && newStart && origStart.getTime() !== newStart.getTime();
  const endChanged    = origEnd   && newEnd   && origEnd.getTime()   !== newEnd.getTime();
  const datesChanged  = startChanged || endChanged;

  const origDays = parseDays(originalBooking);
  const newDays  = parseDays(newBooking);
  const dayDiff  = newDays - origDays;

  // Cost of N extra/fewer days based on the original products' daily rate
  const origDailyRate   = origDays > 0 ? originalProductCostCents / origDays : 0;
  const dayDiffCents    = dayDiff !== 0 ? Math.round(dayDiff * origDailyRate) : null;

  const dateLines = [];
  if (datesChanged) {
    if (startChanged) {
      dateLines.push({ text: `Start date changed from ${formatDate(originalBooking.startDate)} to ${formatDate(newBooking.startDate)}.`, diffCents: null });
    }
    if (endChanged) {
      dateLines.push({ text: `End date changed from ${formatDate(originalBooking.endDate)} to ${formatDate(newBooking.endDate)}.`, diffCents: null });
    }
    if (dayDiff > 0) {
      dateLines.push({ text: `Rental extended by ${dayDiff} day${dayDiff !== 1 ? "s" : ""} (${origDays} → ${newDays} days).`, diffCents: dayDiffCents });
    } else if (dayDiff < 0) {
      dateLines.push({ text: `Rental shortened by ${Math.abs(dayDiff)} day${Math.abs(dayDiff) !== 1 ? "s" : ""} (${origDays} → ${newDays} days).`, diffCents: dayDiffCents });
    }
  }

  // 2. Products
  const origP1 = originalBooking.product1 || [];
  const origP2 = originalBooking.product2 || [];
  const newP1  = newBooking.product1 || [];
  const newP2  = newBooking.product2 || [];

  const totalOrigBikes = origP1.reduce((sum, p) => sum + (p.quantity || 0), 0);
  const totalNewBikes  = newP1.reduce(
    (sum, p) => (!p.removedFromOriginalBooking ? sum + (p.quantity || 0) : sum),
    0
  );
  const bikeDiff = totalNewBikes - totalOrigBikes;

  const productLines = [];

  const allP1Ids = new Set([
    ...origP1.map((p) => p.id),
    ...newP1.filter((p) => !p.removedFromOriginalBooking).map((p) => p.id),
  ]);
  allP1Ids.forEach((id) => {
    const orig    = origP1.find((p) => p.id === id);
    const next    = newP1.find((p) => p.id === id);
    const origQty = orig?.quantity || 0;
    const nextQty = next?.removedFromOriginalBooking ? 0 : (next?.quantity || 0);
    if (origQty === nextQty) return;
    const catalog   = product1Catalog.find((p) => p.id === id);
    const name      = catalog?.name || orig?.name || next?.name || "Item";
    const unitPrice = Number(catalog?.price) || 0; // cents, per-day
    const qtyDiff   = nextQty - origQty;
    const diffCents = unitPrice > 0 ? Math.round(qtyDiff * unitPrice * newDays) : null;
    if (origQty === 0 && nextQty > 0) {
      productLines.push({ text: `Added ${nextQty}x ${name}.`, diffCents });
    } else if (nextQty === 0) {
      productLines.push({ text: `Removed ${origQty}x ${name}.`, diffCents });
    } else {
      const dir = nextQty > origQty ? "Increased" : "Decreased";
      productLines.push({ text: `${dir} ${name} from ${origQty} to ${nextQty}.`, diffCents });
    }
  });

  const allP2Ids = new Set([
    ...origP2.map((p) => p.id),
    ...newP2.filter((p) => !p.removedFromOriginalBooking).map((p) => p.id),
  ]);
  allP2Ids.forEach((id) => {
    const orig    = origP2.find((p) => p.id === id);
    const next    = newP2.find((p) => p.id === id);
    const origQty = orig?.quantity || 0;
    const nextQty = next?.removedFromOriginalBooking ? 0 : (next?.quantity || 0);
    const catalog   = product2Catalog.find((p) => p.id === id);
    const name      = catalog?.name || orig?.name || next?.name || "Add-on";
    const unitPrice = Number(catalog?.price) || 0;
    const opt       = catalog?.option || "per-booking";

    if (origQty !== nextQty) {
      // Quantity changed — show the standard added/removed/changed line
      const qtyDiff = nextQty - origQty;
      let diffCents = null;
      if (unitPrice > 0) {
        if (opt === "per-day")       diffCents = Math.round(qtyDiff * unitPrice * newDays);
        else if (opt === "per-bike") diffCents = Math.round(qtyDiff * unitPrice * totalNewBikes);
        else                         diffCents = Math.round(qtyDiff * unitPrice);
      }
      if (origQty === 0 && nextQty > 0) {
        productLines.push({ text: `Added ${nextQty}x ${name}.`, diffCents, color: COLOR_POSITIVE, larger: true });
      } else if (nextQty === 0) {
        productLines.push({ text: `Removed ${origQty}x ${name}.`, diffCents, color: COLOR_NEGATIVE, larger: true });
      } else {
        const dir = nextQty > origQty ? "Increased" : "Decreased";
        productLines.push({ text: `${dir} ${name} from ${origQty} to ${nextQty}.`, diffCents });
      }
    } else if (nextQty > 0 && unitPrice > 0) {
      // Quantity unchanged — check if cost shifted due to bike count or day count change
      if (opt === "per-bike" && bikeDiff !== 0) {
        const diffCents = Math.round(nextQty * unitPrice * bikeDiff);
        const bikeDir   = bikeDiff > 0 ? `+${bikeDiff}` : `${bikeDiff}`;
        productLines.push({
          text: `${name} adjusted (${nextQty} qty × ${bikeDir} bike${Math.abs(bikeDiff) !== 1 ? "s" : ""}).`,
          diffCents,
        });
      } else if (opt === "per-day" && dayDiff !== 0) {
        const diffCents = Math.round(nextQty * unitPrice * dayDiff);
        const dayDir    = dayDiff > 0 ? `+${dayDiff}` : `${dayDiff}`;
        productLines.push({
          text: `${name} adjusted (${nextQty} qty × ${dayDir} day${Math.abs(dayDiff) !== 1 ? "s" : ""}).`,
          diffCents,
        });
      }
    }
  });

  // 3. Transportation
  const origDelivery  = !!originalBooking.delivery;
  const origPickup    = !!originalBooking.homePickup;
  const newDelivery   = !!newBooking.delivery;
  const newPickup     = !!newBooking.homePickup;

  const origAddress   = (originalBooking.deliveryAddress || "").trim();
  const newAddress    = (newBooking.deliveryAddress || "").trim();
  const addressChanged = origAddress !== newAddress && (newDelivery || newPickup);

  const origMiles = origAddress ? (parseFloat(originalBooking.numMiles) || null) : null;
  const newMiles  = newAddress  ? (parseFloat(newBooking.numMiles)      || null) : null;

  const transportLines = [];

  if (!origDelivery && newDelivery) {
    const net = newDeliveryCents ? Math.round(newDeliveryCents * newTransNetRate) : null;
    transportLines.push({ text: `Delivery added${newAddress ? ` to ${newAddress}` : ""}.`, diffCents: net });
  } else if (origDelivery && !newDelivery) {
    const net = origDeliveryCents ? -Math.round(origDeliveryCents * origTransNetRate) : null;
    transportLines.push({ text: "Delivery removed.", diffCents: net });
  }

  if (!origPickup && newPickup) {
    const net = newPickupCents ? Math.round(newPickupCents * newTransNetRate) : null;
    transportLines.push({ text: `Home pickup added${newAddress ? ` from ${newAddress}` : ""}.`, diffCents: net });
  } else if (origPickup && !newPickup) {
    const net = origPickupCents ? -Math.round(origPickupCents * origTransNetRate) : null;
    transportLines.push({ text: "Home pickup removed.", diffCents: net });
  }

  if (addressChanged && (origDelivery || origPickup)) {
    if (!addressConfirmed) {
      transportLines.push({ text: "Address changed — calculating distance...", diffCents: null });
    } else if (origMiles !== null && newMiles !== null) {
      const mileDiff      = newMiles - origMiles;
      const diffStr       = mileDiff >= 0 ? `+${mileDiff.toFixed(1)} mi` : `${mileDiff.toFixed(1)} mi`;
      // Use net (post-discount) transport costs so the amount matches the total impact
      const activeNewNet  = (newDelivery  ? Math.round(newDeliveryCents  * newTransNetRate)  : 0)
                          + (newPickup    ? Math.round(newPickupCents    * newTransNetRate)  : 0);
      const activeOrigNet = (origDelivery ? Math.round(origDeliveryCents * origTransNetRate) : 0)
                          + (origPickup   ? Math.round(origPickupCents   * origTransNetRate) : 0);
      const addrDiffCents = activeNewNet - activeOrigNet;
      transportLines.push({
        text: `Address changed from ${origMiles.toFixed(1)} mi to ${newMiles.toFixed(1)} mi (${diffStr}).`,
        diffCents: addrDiffCents !== 0 ? addrDiffCents : null,
      });
    } else if (newMiles !== null) {
      transportLines.push({ text: `Address changed — new distance: ${newMiles.toFixed(1)} mi.`, diffCents: null });
    } else {
      transportLines.push({ text: "Address changed — calculating distance...", diffCents: null });
    }
  }

  // If any transport change was made and a named discount is active on the new booking, note it
  const newTransDiscountName = newBooking.transportationDiscount?.discountName || "";
  if (transportLines.length > 0 && newTransDiscountPct > 0 && newTransDiscountName) {
    transportLines.push({ text: newTransDiscountName, diffCents: null, muted: false, color: COLOR_NEGATIVE });
  }

  // 4. Discounts
  const origTransDiscount = originalBooking.transportationDiscount;
  const newTransDiscount  = newBooking.transportationDiscount;
  const origDurDiscount   = originalBooking.durationDiscount;
  const newDurDiscount    = newBooking.durationDiscount;

  const origTransPct  = Number(origTransDiscount?.discountPercent) || 0;
  const newTransPct   = Number(newTransDiscount?.discountPercent)  || 0;
  const origTransName = origTransDiscount?.discountName || "";
  const newTransName  = newTransDiscount?.discountName  || "";

  const origDurPct  = Number(origDurDiscount?.discountPercent) || 0;
  const newDurPct   = Number(newDurDiscount?.discountPercent)  || 0;
  const origDurName = origDurDiscount?.discountName || "";
  const newDurName  = newDurDiscount?.discountName  || "";

  // Discount increases → customer pays less → negative order-total impact
  const transDiffCents = origTransPct !== newTransPct
    ? -(newTransDiscountCents - origTransDiscountCents)
    : null;
  const durDiffCents = origDurPct !== newDurPct
    ? -(newDurDiscountCents - origDurDiscountCents)
    : null;

  const discountLines = [];

  if (origTransPct !== newTransPct) {
    let text;
    let color;
    if (origTransPct === 0 && newTransPct > 0) {
      text = `Transportation discount added: ${newTransPct}%${newTransName ? ` (${newTransName})` : ""}.`;
      color = COLOR_NEGATIVE;
    } else if (newTransPct === 0) {
      text = `Transportation discount removed (was ${origTransPct}%${origTransName ? ` — ${origTransName}` : ""}).`;
      color = undefined;
    } else if (newTransPct > origTransPct) {
      text = `Transportation discount improved from ${origTransPct}% to ${newTransPct}%${newTransName ? ` (${newTransName})` : ""}.`;
      color = COLOR_NEGATIVE;
    } else {
      text = `Transportation discount reduced from ${origTransPct}% to ${newTransPct}%${newTransName ? ` (${newTransName})` : ""}.`;
      color = undefined;
    }
    discountLines.push({ text, diffCents: transDiffCents, color });
  }

  if (origDurPct !== newDurPct) {
    let text;
    let color;
    if (origDurPct === 0 && newDurPct > 0) {
      text = `Duration discount added: ${newDurPct}%${newDurName ? ` (${newDurName})` : ""}.`;
      color = COLOR_NEGATIVE;
    } else if (newDurPct === 0) {
      text = `Duration discount removed (was ${origDurPct}%${origDurName ? ` — ${origDurName}` : ""}).`;
      color = undefined;
    } else if (newDurPct > origDurPct) {
      text = `Duration discount improved from ${origDurPct}% to ${newDurPct}%${newDurName ? ` (${newDurName})` : ""}.`;
      color = COLOR_NEGATIVE;
    } else {
      text = `Duration discount reduced from ${origDurPct}% to ${newDurPct}%${newDurName ? ` (${newDurName})` : ""}.`;
      color = undefined;
    }
    discountLines.push({ text, diffCents: durDiffCents, color });
  }

  // ─── overall "no changes" check ────────────────────────────────────────────
  const hasAnyChange =
    dateLines.length > 0 ||
    productLines.length > 0 ||
    transportLines.length > 0 ||
    discountLines.length > 0;

  // ─── sub-components ──────────────────────────────────────────────────────────

  const Divider = () => (
    <View style={{ height: 1, backgroundColor: DIVIDER_COLOR, marginVertical: 8 }} />
  );

  const SectionTitle = ({ children }) => (
    <Text style={[styles.summarySectionTitle, { marginTop: 0, color: ACCENT }]}>
      {children}
    </Text>
  );

  // Line with optional right-aligned colored amount
  const Line = ({ text, diffCents = null, muted = false, color, larger = false }) => {
    const amountColor = diffCents > 0 ? COLOR_POSITIVE : diffCents < 0 ? COLOR_NEGATIVE : TEXT_MUTED;
    const amountText =
      diffCents == null
        ? null
        : diffCents === 0
        ? formatAmount(0)
        : diffCents > 0
        ? `+${formatAmount(diffCents)}`
        : `−${formatAmount(Math.abs(diffCents))}`;
    const textFontSize = larger ? 16 : 14;

    return (
      <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center", paddingVertical: 3 }}>
        <Text style={{ flex: 1, fontSize: textFontSize, fontWeight: larger ? "600" : "400", color: color ?? (muted ? TEXT_MUTED : TEXT_DARK), lineHeight: larger ? 22 : 20, paddingRight: 8 }}>
          {text}
        </Text>
        {amountText != null && (
          <Text style={{ fontSize: larger ? 15 : 14, fontWeight: "600", color: amountColor }}>
            {amountText}
          </Text>
        )}
      </View>
    );
  };

  // ─── render ──────────────────────────────────────────────────────────────────

  return (
    <View
      style={[
        styles.section,
        {
          backgroundColor: BG,
          borderColor: BORDER,
          borderWidth: 2,
          borderRadius: 6,
          padding: 16,
        },
      ]}
    >
      <Text style={styles.sectionTitle}>What Changed</Text>

      {!hasAnyChange ? (
        <Line text="No changes made." muted />
      ) : (
        <>
          {/* ── Rental Dates ── */}
          {dateLines.length > 0 && (
            <>
              <SectionTitle>Rental Dates</SectionTitle>
              {dateLines.map((line, i) => (
                <Line key={`date-${i}`} text={line.text} diffCents={line.diffCents} />
              ))}
              <Divider />
            </>
          )}

          {/* ── Products Rented ── */}
          {productLines.length > 0 && (
            <>
              <SectionTitle>Products Rented</SectionTitle>
              {productLines.map((line, i) => (
                <Line key={`prod-${i}`} text={line.text} diffCents={line.diffCents} color={line.color} larger={line.larger} />
              ))}
              <Divider />
            </>
          )}

          {/* ── Transportation ── */}
          {transportLines.length > 0 && (
            <>
              <SectionTitle>Transportation</SectionTitle>
              {transportLines.map((line, i) => (
                <Line key={`trans-${i}`} text={line.text} diffCents={line.diffCents} muted={!!line.muted} color={line.color} />
              ))}
              <Divider />
            </>
          )}

          {/* ── Discounts ── (verbiage only, no currency) */}
          {discountLines.length > 0 && (
            <>
              <SectionTitle>Discounts</SectionTitle>
              {discountLines.map((line, i) => (
                <Line key={`disc-${i}`} text={line.text} color={line.color} />
              ))}
              <Divider />
            </>
          )}

        </>
      )}

      <Text style={{ fontSize: 12, color: TEXT_MUTED, marginTop: 12, fontStyle: "italic" }}>
        Sales tax and card processing fees for payments/refunds apply.
      </Text>
    </View>
  );
};

export default VerbalOrderChanges;
